import { useCallback, useMemo, useState } from "react";
import { Image, Pressable, StyleSheet, Text, useWindowDimensions, View } from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter, type Href } from "expo-router";
import { useTranslation } from "react-i18next";

import { monthlyVibeCardApi } from "@/api";
import { Spacing, type ThemeColors } from "@/constants/theme";
import { useScheme, useTheme, type Scheme } from "@/hooks/use-theme";
import { monthLabel } from "@/lib/month-label";
import { useAuthStore } from "@/state/auth-store";
import { useAppTheme, type AppColors } from "@/theme/app-theme";
import type { MonthlyVibeCardStatusResponse } from "@/types";

type ProfileVibeCardEntryProps = {
  compactLabel?: string;
  replaceOnNavigate?: boolean;
  variant?: "card" | "button";
};

// The 384px lossless webp, not the 1254px master PNG it is derived from. The
// slot is 92x112pt, so 384 covers @3x with headroom — the device was already
// downscaling the 1.2 MB PNG to this size at runtime. Regenerate from the
// master (same directory) if the artwork changes:
//   sharp(png).resize(384, 384, {fit:"inside"}).webp({lossless:true})
const MOOD_JAR_IMAGE = require("../../../assets/mood_jar-transparent.webp");

/**
 * Compact Monthly Vibe Card entry. It reflects the month's state —
 * collecting / ready / available / not-enough-evidence — and routes to the Mood
 * Jar (to add moods) or the card screen (to reveal/view). It makes ONE
 * read-only status call and never triggers generation.
 */
export function ProfileVibeCardEntry({
  compactLabel,
  replaceOnNavigate = false,
  variant = "card",
}: ProfileVibeCardEntryProps) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { colors } = useAppTheme();
  const dashboardColors = useTheme();
  const scheme = useScheme();
  const { width } = useWindowDimensions();
  const compact = width < 520;
  const vibeColors = useMemo(
    () => getVibeCardColors(dashboardColors, scheme),
    [dashboardColors, scheme],
  );
  const styles = useMemo(
    () => createStyles(colors, vibeColors, compact),
    [colors, vibeColors, compact],
  );

  const [status, setStatus] = useState<MonthlyVibeCardStatusResponse | null>(null);

  // Refetch whenever Profile regains focus, so a card that just became ready
  // (e.g. after adding moods or seeding) shows up without a full reload.
  //
  // Gated on the session. `/me/monthly-vibe-cards/status` is an authenticated
  // endpoint, and this used to call it unconditionally: measured on the web
  // export, opening `/business` signed out fired it during the frames before
  // the auth guard redirected to `/welcome`, and it came back 401 — the only
  // console error on the surface, recorded in BASELINE-WEB.md. Nothing was
  // visibly broken because the `.catch` below swallowed it, which is precisely
  // why it survived: a request that can only fail, on the first-paint path,
  // reporting a 401 to Sentry for a user who is simply not signed in.
  const isAuthed = useAuthStore((s) => s.status === "authenticated");

  useFocusEffect(
    useCallback(() => {
      if (!isAuthed) return undefined;
      let cancelled = false;
      monthlyVibeCardApi
        .status()
        .then((s) => {
          if (!cancelled) setStatus(s);
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }, [isAuthed]),
  );

  const latest = status?.latest;
  const latestMonth = latest ? monthLabel(latest.periodKey, i18n.language) : "";

  // Decide the headline copy + primary action from the latest completed month,
  // falling back to the collecting state for the current month.
  let title = t("profile.monthlyVibeCard.entry.title");
  let subtitle = t("profile.monthlyVibeCard.entry.collectingSubtitle");
  let collectingProgress = t("profile.monthlyVibeCard.entry.collectingProgress");
  let buttonLabel = t("profile.monthlyVibeCard.entry.addMoods");
  const moodJarHref = replaceOnNavigate ? "/mood-jar?returnTo=dashboard" : "/mood-jar";
  const goTo = (href: Href) => {
    if (replaceOnNavigate) {
      router.replace(href);
      return;
    }

    router.push(href);
  };
  let onPress = () => goTo(moodJarHref);

  if (latest?.status === "ready") {
    title = t("profile.monthlyVibeCard.entry.readyTitle", { month: latestMonth });
    subtitle = t("profile.monthlyVibeCard.entry.readySubtitle");
    buttonLabel = t("profile.monthlyVibeCard.entry.reveal");
    onPress = () => goTo(`/vibe-card?period=${latest.periodKey}`);
  } else if (latest?.status === "available") {
    title = t("profile.monthlyVibeCard.entry.availableTitle", { month: latestMonth });
    subtitle = t("profile.monthlyVibeCard.entry.availableSubtitle");
    buttonLabel = t("profile.monthlyVibeCard.entry.view");
    onPress = () => goTo(`/vibe-card?period=${latest.periodKey}`);
  } else if (latest?.status === "insufficient_data") {
    title = t("profile.monthlyVibeCard.entry.title");
    subtitle = t("profile.monthlyVibeCard.entry.dashboardInsufficientDescription");
    collectingProgress = t(
      "profile.monthlyVibeCard.entry.dashboardInsufficientDisclaimer",
    );
    buttonLabel = t("profile.monthlyVibeCard.entry.addMoods");
    onPress = () => goTo(moodJarHref);
  }

  const wrappedSubtitle = useMemo(
    () => splitWrappedText(subtitle, compact ? 62 : subtitle.length),
    [subtitle, compact],
  );

  if (variant === "button") {
    const buttonOnlyLabel =
      compactLabel ??
      (latest?.status === "available"
        ? t("profile.monthlyVibeCard.entry.view")
        : t("profile.monthlyVibeCard.entry.reveal"));
    const buttonOnlyRoute =
      latest?.status === "ready" ||
      latest?.status === "available"
        ? `/vibe-card?period=${latest.periodKey}`
        : "/vibe-card";

    return (
      <Pressable
        onPress={() => {
          if (replaceOnNavigate) {
            router.replace(buttonOnlyRoute);
            return;
          }

          router.push(buttonOnlyRoute);
        }}
        accessibilityRole="button"
        accessibilityLabel={buttonOnlyLabel}
        style={styles.profileButton}
      >
        <Ionicons name="gift-outline" size={18} color={colors.primary} />
        <Text style={styles.profileButtonText} numberOfLines={1}>
          {buttonOnlyLabel}
        </Text>
        <Ionicons name="arrow-forward" size={16} color={colors.primary} />
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityHint={buttonLabel}
      style={styles.cardPressable}
    >
      <LinearGradient
        colors={vibeColors.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.card}
      >
        <View style={styles.topRow}>
          <View style={styles.jarColumn}>
            <Image
              source={MOOD_JAR_IMAGE}
              resizeMode="contain"
              style={styles.jarImage}
              accessible={false}
            />
          </View>

          <View style={styles.cardCopy}>
            <View style={styles.headerCopy}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.subtitle}>
                {compact ? wrappedSubtitle.leading : subtitle}
              </Text>
            </View>

            {status && !compact ? (
              <View style={styles.teaserRow}>
                <Text style={styles.teaser} numberOfLines={3}>
                  {collectingProgress}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        {compact && wrappedSubtitle.trailing ? (
          <Text style={styles.subtitleWrap}>{wrappedSubtitle.trailing}</Text>
        ) : null}

        {status && compact ? (
          <View style={styles.teaserRow}>
            <Text style={styles.teaser} numberOfLines={3}>
              {collectingProgress}
            </Text>
          </View>
        ) : null}

        <View style={styles.button}>
          <Text style={styles.buttonText} numberOfLines={1}>
            {buttonLabel}
          </Text>
          <Ionicons name="arrow-forward" size={18} color={vibeColors.tint} />
        </View>
      </LinearGradient>
    </Pressable>
  );
}

function getVibeCardColors(d: ThemeColors, scheme: Scheme) {
  if (scheme === "dark") {
    return {
      gradient: ["#132C42", "#0D2236"] as const,
      border: "#2A4560",
      title: "#F4F8FF",
      subtitle: "#AFC1D8",
      teaserText: "#C2D0E2",
      teaserBackground: "rgba(5, 18, 31, 0.54)",
      buttonBackground: "rgba(3, 18, 35, 0.28)",
      tint: "#5FA0FF",
    };
  }

  return {
    gradient: [d.surface, d.surfaceAlt] as const,
    border: d.border,
    title: d.text,
    subtitle: d.textSecondary,
    teaserText: d.textSecondary,
    teaserBackground: d.backgroundSelected,
    buttonBackground: d.surface,
    tint: d.tint,
  };
}

type VibeCardColors = ReturnType<typeof getVibeCardColors>;

function splitWrappedText(text: string, leadingLimit: number) {
  const words = text.trim().split(/\s+/);
  let leading = "";
  let splitIndex = 0;

  for (let index = 0; index < words.length; index += 1) {
    const next = leading ? `${leading} ${words[index]}` : words[index];
    if (next.length > leadingLimit && leading) break;

    leading = next;
    splitIndex = index + 1;
  }

  return {
    leading: leading || text,
    trailing: words.slice(splitIndex).join(" "),
  };
}

function createStyles(c: AppColors, v: VibeCardColors, compact: boolean) {
  return StyleSheet.create({
    cardPressable: {
      borderRadius: 20,
      overflow: "hidden",
      marginVertical: compact ? Spacing.lg : 10,
    },
    card: {
      minHeight: 188,
      gap: 10,
      paddingHorizontal: 18,
      paddingVertical: 18,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: v.border,
    },
    topRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 6,
    },
    jarColumn: {
      width: 96,
      alignItems: "center",
      justifyContent: "flex-start",
      flexShrink: 0,
    },
    jarImage: {
      width: 92,
      height: 112,
    },
    cardCopy: {
      flex: 1,
      minWidth: 0,
      gap: 14,
      paddingTop: 2,
    },
    headerCopy: { gap: 8 },
    title: { fontSize: 18, fontWeight: "800", color: v.title },
    subtitle: {
      fontSize: compact ? 12 : 14,
      color: v.subtitle,
      lineHeight: compact ? 17 : 20,
    },
    subtitleWrap: {
      fontSize: 12,
      color: v.subtitle,
      lineHeight: 17,
    },
    teaserRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 12,
      paddingVertical: 9,
      borderRadius: 16,
      backgroundColor: v.teaserBackground,
    },
    teaser: {
      fontSize: compact ? 11 : 13,
      fontStyle: "italic",
      color: v.teaserText,
      lineHeight: compact ? 15 : 17,
    },
    button: {
      alignSelf: "center",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      minHeight: 48,
      paddingHorizontal: 18,
      borderRadius: 14,
      backgroundColor: v.buttonBackground,
      borderWidth: 1,
      borderColor: v.tint,
    },
    buttonText: {
      flexShrink: 1,
      fontSize: compact ? 14 : 15,
      fontWeight: "800",
      color: v.tint,
    },
    profileButton: {
      alignSelf: "stretch",
      minHeight: 42,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.primaryBorder,
      backgroundColor: c.primarySurface,
    },
    profileButtonText: {
      flexShrink: 1,
      fontSize: 14,
      fontWeight: "700",
      color: c.primary,
    },
  });
}
